/* eslint-disable @typescript-eslint/no-explicit-any, max-lines */
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import includes from 'lodash/includes';

import { setCookie } from '@cian/utils/lib/browser/cookie';

import * as Analytics from '../../utils/analytics_helper';
import { matchQueryParams } from './query_params_matcher';
import { renderFilters } from './search_config';
import { IReduxState } from '../../types/redux';
import { shouldNewFiltersShow } from '../../utils/filters';
import { IUser } from '../../types/user';
import { IHousingInfo } from '../../types/housing_info';
import { ISearchType } from '../../types/filters/type';
import { ISquare } from '../../types/filters/square';
import { IBuildingTypes, IBuildingTypesConfig } from '../../types/filters/building_types';
import { IRooms } from '../../types/filters/rooms';
import { ISearchPrice } from '../../types/filters/price';
import { IYear } from '../../types/filters/year';
import { IBedroom } from '../../types/filters/bedroom';
import { IRegionsData } from '../../types/regions_data';
import { IUserGeo } from '../../types/filters/user_geo';
import { ISearchConfig } from '../../types/search';
import { ISuburbanOfferFilter } from '../../types/filters/suburban_offer_filter';
import { IDomain } from '../../reducers/modules/domain/domain';
import { getSearchUrl, IJsonQueryTerms, getMapUrl } from '../../reducers/modules/search/search';
import { getHousingInfo } from '../../reducers/modules/housing_info';
import { SearchFilters } from '../../components/search_filters/search_filters';
import { FiltersBlock } from '../../components/filters_block/filters_block';
import { getFiltersConfiguration } from '../../utils/ab_mainpage_newbuilding_search';
import { declineNumeric, formatPrice } from '../../utils/format_helper';
import { TypeFilter } from '../filters/type_filter';
import { BuildingTypeFilter } from '../filters/building_type_filter';
import { sendAdsEvent } from '../../utils/tracking/sendAdsEvent';

interface IProps {
  abGroup: number;
  // tslint:disable-next-line: no-any
  dispatch?: Dispatch<any>;
  housingInfo: IHousingInfo;
  type: ISearchType;
  buildingType: IBuildingTypes;
  actualBuildingType: IBuildingTypes;
  square: ISquare;
  subdomain: string;
  rooms: IRooms;
  price: ISearchPrice;
  userGeo: IUserGeo;
  bedroom: IBedroom;
  year: IYear;
  regionsData: IRegionsData;
  domain: IDomain;
  section: string;
  title: string;
  user: IUser;
  searchConfig: ISearchConfig;
  buildingTypes: IBuildingTypesConfig;
  suburbanOfferFilter: ISuburbanOfferFilter;
  isExperimentsLoading: boolean;
  isNewFilters: boolean;
  isDobroshrift: boolean;
}

interface IState {
  isApartment?: boolean;
  backgroundImageUrl?: string;
  backgroundShown: boolean;
  mediaNote?: string;
}

interface IFilterProps {
  section: string;
}

interface ISearchUrl {
  searchUrl: string;
  queryStringOrPathName: string;
}

export class FiltersComponent extends React.Component<IProps, IState> {
  private timer: NodeJS.Timer | number;

  public constructor(props: IProps) {
    super(props);
    this.onLoadAdfox = this.onLoadAdfox.bind(this);
    this.state = {
      backgroundShown: false,
    };
  }

  public componentDidMount() {
    this.timer = setTimeout(() => {
      this.setState({
        backgroundImageUrl: undefined,
        backgroundShown: true,
        isApartment: false,
      });
    }, 2000);
  }

  public submitSearch() {
    if (!this.props.dispatch) {
      return;
    }

    const params = matchQueryParams(
      this.props.type,
      this.props.actualBuildingType,
      this.props.square,
      this.props.rooms,
      this.props.price,
      this.props.userGeo,
      this.props.bedroom,
      this.props.year,
      this.props.section === 'new-buildings',
      this.props.suburbanOfferFilter,
    );

    const topRegions = [1, 2, 4593, 4588, -1, -2];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.props.dispatch(getSearchUrl(params)) as any).then((url: ISearchUrl) => {
      let searchUrl = url.searchUrl;
      // istanbul ignore next
      if (
        (params.region && includes(topRegions, (params.region as IJsonQueryTerms<number>).value[0])) ||
        this.props.section === 'new-buildings'
      ) {
        searchUrl = this.replaceSearchPath(params._type, searchUrl);
      }

      Analytics.trackSearchEvent(
        'button_search',
        this.props.section,
        url.queryStringOrPathName,
        params,
        this.props.user,
        this.props.abGroup,
      );

      /**
       * Ставим сессионую гео-куку на основе полученных данных от гео фильтра
       */
      const { regionId, mainTownId } = this.props.userGeo.userGeoDefinitionWithGeoFilter;

      if (regionId) {
        setCookie('session_region_id', String(regionId), { secure: false });
      }

      if (mainTownId) {
        setCookie('session_main_town_region_id', String(mainTownId), { secure: false });
      }

      window.location.href = searchUrl;
    });
  }
  // istanbul ignore next
  public submitMapSearch() {
    if (!this.props.dispatch) {
      return;
    }

    const params = matchQueryParams(
      this.props.type,
      this.props.actualBuildingType,
      this.props.square,
      this.props.rooms,
      this.props.price,
      this.props.userGeo,
      this.props.bedroom,
      this.props.year,
      this.props.section === 'new-buildings',
      this.props.suburbanOfferFilter,
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.props.dispatch(getMapUrl(params)) as any).then((url: string) => {
      Analytics.trackSearchEvent('search_on_map', this.props.section, url, params, this.props.user, this.props.abGroup);

      Analytics.clickSearchOnMapButtonOnFilter(this.props.section, url);

      window.location.href = url;
    });
  }

  public render() {
    let mediaInfoText: string | undefined;
    const hi = this.props.housingInfo;
    // istanbul ignore next
    if (hi.offers_count) {
      mediaInfoText = `В продаже ${formatPrice(hi.offers_count)}
     ${declineNumeric(
       hi.offers_count,
       this.state.isApartment ? ['апартамент', 'апартамента', 'апартаментов'] : ['квартира', 'квартиры', 'квартир'],
     )} по цене от ${formatPrice(hi.min_price2)} ₽`;
    }

    return (
      <FiltersBlock
        section={this.props.section}
        backgroundShown={this.state.backgroundShown}
        mediaInfoText={mediaInfoText}
        backgroundImageUrl={this.state.backgroundImageUrl}
        mediaNote={this.state.mediaNote}
        onLoadAdfox={this.onLoadAdfox}
        onBannerLinkClick={this.onBannerLinkClick}
        isExperimentsLoading={this.props.isExperimentsLoading}
        isNewFilters={this.props.isNewFilters}
      >
        <SearchFilters
          isDobroshrift={this.props.isDobroshrift}
          title={this.props.title}
          onSubmit={
            // istanbul ignore next
            () => this.submitSearch()
          }
          onMapSubmit={
            // istanbul ignore next
            () => this.submitMapSearch()
          }
        >
          {this.props.section !== 'new-buildings' && (
            <TypeFilter buildingTypes={this.props.buildingTypes} searchConfig={this.props.searchConfig} />
          )}
          {this.props.section !== 'new-buildings' && (
            <BuildingTypeFilter
              buildingType={this.props.buildingType.buildingType}
              buildingTypes={this.props.buildingTypes}
              searchConfig={this.props.searchConfig}
            />
          )}
          {renderFilters(
            this.props.type.searchType,
            this.props.buildingType.buildingType,
            this.props.section === 'new-buildings',
            this.props.searchConfig,
          )}
        </SearchFilters>
      </FiltersBlock>
    );
  }
  // istanbul ignore next
  private onLoadAdfox(context: {
    newBuildingId: string;
    isApartment: string;
    bgImageUrl: string;
    note: string;
    analyticsBannerId: string;
  }) {
    const { user, section } = this.props;

    if (this.props.dispatch) {
      if (!this.props.housingInfo.isFetching) {
        this.props.dispatch(getHousingInfo(void 0, context.newBuildingId));
      }
      clearTimeout(this.timer as number);
      this.setState({
        backgroundImageUrl: context.bgImageUrl,
        backgroundShown: true,
        isApartment: !!+context.isApartment,
        mediaNote: context.note,
      });

      sendAdsEvent({
        realtyUserId: user ? user.userId : undefined,
        action: 'show',
        bannerId: context.analyticsBannerId,
        section,
      });
    }
  }

  private onBannerLinkClick = (context?: { analyticsBannerId?: string }) => {
    const { user, section } = this.props;

    if (!context) {
      return;
    }

    sendAdsEvent({
      realtyUserId: user ? user.userId : undefined,
      action: 'click',
      bannerId: context.analyticsBannerId,
      section,
    });
  };
  // istanbul ignore next
  private replaceSearchPath(type: string, url: string) {
    const searchUrlRe = this.props.section === 'new-buildings' ? /\/newobjects\/list\// : /\/cat\.php/;
    const filtersFormUrl = this.props.section === 'new-buildings' ? '/newobjects/search/' : '/nd/search/';

    return url.replace(searchUrlRe, filtersFormUrl);
  }
}

export const mapStateToProps = (state: IReduxState, props: IFilterProps): IProps => {
  const { section } = props;
  const filtersConfiguration = getFiltersConfiguration(state, section);

  const isNewFilters = shouldNewFiltersShow({ section });

  return {
    ...filtersConfiguration,
    abGroup: state.config.abGroup,
    bedroom: state.bedroom,
    domain: state.domain,
    housingInfo: state.housingInfo,
    isExperimentsLoading: state.mainpage.experiments === undefined,
    price: state.price,
    regionsData: state.mainpage.regionsData,
    rooms: state.rooms,
    section,
    square: state.square,
    subdomain: state.config.subdomain,
    title: state.mainpage.seoData.h1,
    type: state.type,
    user: state.user,
    userGeo: state.userGeo,
    year: state.year,
    suburbanOfferFilter: state.suburbanOfferFilter,
    isNewFilters,
    isDobroshrift: state.features.isDobroshriftEnabled,
  };
};

export const Filters = connect(mapStateToProps)(FiltersComponent);
